

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card,
    Grid,
    Icon,
    Snackbar,
    Alert,
    CircularProgress,
    Chip
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import DataTable from "examples/Tables/DataTable";
import TableSkeleton from 'components/CustomSkeleton/TableSkeleton';
import EmptyState from 'components/EmptyTable/EmptyState';
import { useAuth } from 'auth/Auth';
import { usePermission } from 'auth/usePermission';
import NewTravelOrderItemModal from 'components/NewTravelOrderItemModal';
import { useCurrency } from 'context/CurrencyContext';

const TravelOrderItems = () => {
    const [items, setItems] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const { axiosInstance } = useAuth();
    const { formatCurrency, currency: systemCurrency } = useCurrency();
    const navigate = useNavigate();
    const { canCreate } = usePermission('travel_order_items');

    useEffect(() => {
        fetchItems();
    }, []);

    const fetchItems = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.get('/travel-order-items/');
            setItems(response.data);
        } catch (error) {
            console.error('Error fetching travel order items:', error);
            setSnackbar({ open: true, message: 'Error loading travel order items', severity: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenModal = () => setIsModalOpen(true);
    const handleCloseModal = () => setIsModalOpen(false);

    const handleSaveItem = async (payload) => {
        try {
            await axiosInstance.post('/travel-order-items/', payload);
            setSnackbar({ open: true, message: 'Travel Order Item created successfully', severity: 'success' });
            fetchItems();
        } catch (error) {
            console.error('Error saving travel order item:', error);
            throw error;
        }
    };

    const handleRowClick = (rowData) => {
        navigate(`/travel-order-item/${rowData.id}`);
    };

    const columns = [
        {
            Header: "Item Name",
            accessor: "name",
            width: "15%",
            Cell: ({ value, row }) => (
                <MDTypography
                    component="span"
                    variant="caption"
                    color="info"
                    fontWeight="medium"
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleRowClick(row.original)}
                >
                    {value}
                </MDTypography>
            )
        },
        {
            Header: "Travel Order",
            accessor: "travel_order_id",
            width: "12%",
            Cell: ({ row }) => row.original.travel_order?.name || row.original.travel_order_id
        },
        {
            Header: "Trip Logistics",
            accessor: "trip_component_id",
            width: "15%",
            Cell: ({ row }) => row.original.trip_component?.name || row.original.trip_component_id
        },
        {
            Header: "Merchant",
            accessor: "merchant_id",
            width: "12%",
            Cell: ({ row }) => row.original.merchant?.name || row.original.merchant_id || '-'
        },
        {
            Header: "Pax Count",
            accessor: "pax_count",
            width: "8%"
        },
        {
            Header: "Unit Price",
            accessor: "unit_price",
            width: "10%",
            Cell: ({ value, row }) => value != null && value !== '' ? formatCurrency(value, row.original?.travel_order?.currency || systemCurrency) : '-'
        },
        {
            Header: "Fulfillment Status",
            accessor: "fulfillment_status",
            width: "12%",
            Cell: ({ value }) => (
                <Chip
                    label={value || 'Pending'}
                    size="small"
                    color={
                        value === 'Confirmed' ? 'success' :
                        value === 'Cancelled' ? 'error' :
                        value === 'Waitlisted' ? 'warning' : 'default'
                    }
                />
            )
        },
        {
            Header: "Service Start",
            accessor: "service_start",
            width: "14%",
            Cell: ({ value }) => value ? new Date(value).toLocaleDateString() : '-'
        },
    ];

    const tableData = useMemo(() => ({
        columns,
        rows: items,
    }), [items]);

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={6}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox
                                mx={2}
                                mt={-3}
                                py={3}
                                px={2}
                                variant="gradient"
                                bgColor="info"
                                borderRadius="lg"
                                coloredShadow="info"
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <MDTypography variant="h6" color="white">Travel Order Items</MDTypography>
                                <MDButton
                                    variant="gradient"
                                    color="dark"
                                    onClick={handleOpenModal}
                                    disabled={!canCreate}
                                >
                                    <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                                    &nbsp;Add Item
                                </MDButton>
                            </MDBox>
                            <MDBox pt={3}>
                                {isLoading ? (
                                    <TableSkeleton />
                                ) : items.length > 0 ? (
                                    <DataTable
                                        table={tableData}
                                        isSorted={true}
                                        entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
                                        canSearch
                                        showTotalEntries
                                        noEndBorder
                                    />
                                ) : (
                                    <EmptyState moduleName="Travel Order Item" canCreate={canCreate} />
                                )}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>
            

            <NewTravelOrderItemModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSave={handleSaveItem}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </DashboardLayout>
    );
};

export default TravelOrderItems;

